import { makeAutoObservable } from 'mobx';
import Pages from '../pages/Pages';
import appStore from './appStore';
import { signOutEverywhere } from '../auth/logout';
import Icon from '../../misc/Icon';
import { renderToStaticMarkup } from 'react-dom/server';
import roles from '../roles/roles';
import security from '../roles/security';

const defaultMenu = {
  report: {
    text: 'Report',
    iconType: 'string',
    icon: renderToStaticMarkup(Icon.iconDownload()),
    roles: [roles.admin, roles.dev],
    onClick: (...args) => Pages.report.forward(...args),
  },
  listings: {
    text: 'Drug Listings',
    iconType: 'string',
    icon: renderToStaticMarkup(Icon.iconCards()),
    roles: [roles.admin, roles.dev, roles.orgAdmin],
    onClick: (...args) => Pages.listings.forward(...args),
  },
  wacs: {
    text: 'WAC History',
    iconType: 'string',
    icon: renderToStaticMarkup(Icon.iconGraph()),
    roles: [roles.admin, roles.dev, roles.orgAdmin],
    onClick: (...args) => Pages.wacs.forward(...args),
  },
  approvals: {
    text: 'Approval History',
    iconType: 'string',
    icon: renderToStaticMarkup(Icon.iconCheckbox()),
    roles: [roles.admin, roles.dev, roles.orgAdmin],
    onClick: (...args) => Pages.approvals.forward(...args),
  },
  patents: {
    text: 'Patent History',
    iconType: 'string',
    icon: renderToStaticMarkup(Icon.iconStar()),
    roles: [roles.admin, roles.dev, roles.orgAdmin],
    onClick: (...args) => Pages.patents.forward(...args),
  },
  users: {
    text: 'Users',
    iconType: 'string',
    icon: renderToStaticMarkup(Icon.iconUsers()),
    roles: [roles.admin, roles.dev, roles.orgAdmin],
    onClick: (...args) => {
      // Administrators/Developers may be viewing a company-scoped sublist (via
      // "View Users" on the Companies page). Clicking the main-menu Users link
      // clears that transient company selection so the FULL user list shows
      // again. Org Administrators stay scoped to their own org.
      if (security.hasAnyRole([roles.admin, roles.dev])) {
        appStore.currentOrg = undefined;
      }
      return Pages.users.forward(...args);
    },
  },
  companies: {
    text: 'Companies',
    iconType: 'string',
    icon: renderToStaticMarkup(Icon.iconSuitcase()),
    roles: [roles.admin, roles.dev],
    onClick: (...args) => Pages.companies.forward(...args),
  },
  company: {
    text: 'Company',
    iconType: 'string',
    icon: renderToStaticMarkup(Icon.iconSuitcase()),
    roles: [roles.orgAdmin],
    onClick: (...args) => Pages.singleCompany.forward(...args),
  },
  switch: {
    text: 'Switch Company',
    onClick: () => (appStore.showMessage = true),
  },
  signOut: {
    onClick: (event) => {
      // Forward the click event so signOutEverywhere can preventDefault the
      // menu link's native action (which would otherwise refresh the page
      // mid-logout) before deleting local data/cookies and redirecting.
      signOutEverywhere(event);
    },
    text: 'Sign Out',
  },
};

export default new (class {
  mainMenu = [];

  constructor() {
    makeAutoObservable(this);
    this.setupMenu();
  }

  setupMenu = () =>
    (this.mainMenu = Object.keys(defaultMenu)
      .filter((menuKey) => {
        // When the user's only org-administered company is back in review,
        // every feature is gated off; only the Sign Out control remains.
        if (appStore.underReview) {
          return menuKey === 'signOut';
        }

        let allow = true;

        if (defaultMenu[menuKey].roles) {
          allow = security.hasAnyRole(defaultMenu[menuKey].roles);
        }

        // Company Info is Org-Administrator-only; holding Administrator hides
        // it (same predicate as the /company route guard).
        if (menuKey === 'company') {
          allow = security.canManageCompanyInfo();
        }

        if (menuKey === 'switch') {
          const uniqueOrgCount = new Set(
            appStore.loggedInUser?.roles?.map((role) => role.org),
          ).size;
          allow =
            !security.hasAnyRole([roles.dev, roles.admin]) &&
            appStore.loggedInUser?.roles
              ? uniqueOrgCount > 1
              : false;
        }

        return allow;
      })
      .map((menuKey) => defaultMenu[menuKey]));
})();
